/**
 * Sesotho: Common words
 *
 * What this file covers: Buttons, statuses, table controls, short notices, countries, phone numbers, password
 * rules and contact attributes used on many pages.
 *
 * Tone: The shortest everyday words. Buttons are verbs (Save, Cancel, Remove). Use one word per idea and never
 * vary it, because these words sit beside every other file's text.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const common: Catalog = {
  'action.signOut': 'Tsoa',
  'action.save': 'Boloka',
  'action.cancel': 'Hlakola',
  'action.create': 'Theha',
  'action.delete': 'Hlakola',
  'action.search': 'Batla',
  'common.loading': 'Ea kenya…',
  'common.language': 'Puo',
  'common.unknownError': 'Phoso e sa tsejoeng',
  'common.name': 'Lebitso',
  'common.email': 'Imeile',
  'common.status': 'Boemo',
  'common.type': 'Mofuta',
  'common.description': 'Tlhaloso',
  'common.created': 'E thehiloe',
  'common.updated': 'E nchafalitsoe',
  'common.actions': 'Liketso',
  'common.yes': 'E',
  'common.no': 'Che',
  'common.all': 'Tsohle',
  'common.none': 'Ha ho letho',
  'common.search': 'Batla',
  'common.searchPlaceholder': 'Batla…',
  'common.noResults': 'Ha ho liphetho.',
  'common.date': 'Letsatsi',
  'common.amount': 'Chelete',
  'common.total': 'Kakaretso',
  'common.details': 'Lintlha',
  'common.overview': 'Kakaretso',
  'common.optional': 'Ka boikhethelo',
  'common.required': 'Ea hlokahala',
  'common.copied': 'E kopitsitsoe.',
  'common.enabled': 'E lumelletsoe',
  'common.disabled': 'E thibetsoe',
  'common.never': 'Le ka mohla',
  'common.unknown': 'Ha e tsejoe',
  'common.select': 'Khetha…',
  'common.you': 'Uena',
  'common.notAvailable': 'ha e fumanehe',
  'action.edit': 'Fetola',
  'action.close': 'Koala',
  'action.refresh': 'Nchafatsa',
  'action.retry': 'Leka hape',
  'action.back': 'Morao',
  'action.apply': 'Sebelisa',
  'action.clear': 'Hlakola',
  'action.copy': 'Kopitsa',
  'action.view': 'Sheba',
  'action.confirm': 'Netefatsa',
  'action.add': 'Eketsa',
  'action.remove': 'Tlosa',
  'action.submit': 'Romela',
  'action.send': 'Romela',
  'action.continue': 'Tsoela pele',
  'action.next': 'E latelang',
  'action.previous': 'E fetileng',
  'action.download': 'Khoasolla',
  'action.saveChanges': 'Boloka liphetoho',
  'action.discard': 'Lahla',
  'action.select': 'Khetha',
  'action.open': 'Bula',
  'action.enable': 'Lumella',
  'action.disable': 'Thibela',
  'action.activate': 'Sebetsa',
  'action.pause': 'Emisa nakoana',
  'action.start': 'Qala',
  'action.signIn': 'Kena',
  'action.signUp': 'Ingolisa',
  'action.export': 'Romela ka ntle',
  'action.import': 'Kenya',
  'action.revoke': 'Hlakola',
  'action.regenerate': 'Theha hape',
  'action.keep': 'Boloka',
  'status.active': 'E sebetsa',
  'status.inactive': 'Ha e sebetse',
  'status.pending': 'E emetse',
  'status.approved': 'E amohetsoe',
  'status.rejected': 'E hanoe',
  'status.failed': 'E hlolehile',
  'status.sent': 'E rometsoe',
  'status.delivered': 'E fihlisitsoe',
  'status.read': 'E baliloe',
  'status.draft': 'Moralo',
  'status.submitted': 'E rometsoe',
  'status.paused': 'E emisitsoe',
  'status.running': 'Ea sebetsa',
  'status.completed': 'E phethiloe',
  'status.cancelled': 'E hlakotsoe',
  'status.queued': 'E moleng',
  'status.scheduled': 'E reriloe',
  'status.expired': 'E felile',
  'status.revoked': 'E hlakotsoe',
  'status.verified': 'E netefalitsoe',
  'status.unverified': 'Ha e netefatsoa',
  'status.open': 'E buloe',
  'status.closed': 'E koetsoe',
  'status.resolved': 'E rarollotsoe',
  'status.archived': 'E bolokiloe',
  'status.unknown': 'Ha e tsejoe',
  'toast.saved': 'E bolokiloe',
  'toast.deleted': 'E hlakotsoe',
  'toast.copied': 'E kopitsitsoe',
  'toast.validation': 'Netefatso',
  'toast.saveFailed': 'Ho boloka ho hlolehile',
  'toast.deleteFailed': 'Ho hlakola ho hlolehile',
  'toast.loadFailed': 'Ha ea khona ho kenya',
  'notFound.title': 'Leqephe ha le fumanehe',
  'notFound.backToDashboard': 'Khutlela ho deshboard',
  'country.za': 'Afrika Boroa',
  'country.ng': 'Nigeria',
  'country.ke': 'Kenya',
  'action.undo': 'Khansela',
  'action.dismiss': 'Koala',
  'action.loadMore': 'Kenya tse ling',
  'attributes.group.contact': 'Mokopani',
  'attributes.group.fields': 'Masimo a hao',
  'attributes.group.flow': 'Diphetoho tsa phallo',
  'attributes.contact.name': 'Lebitso',
  'attributes.contact.email': 'Imeile',
  'attributes.contact.msisdn': 'Nomoro ya mohala',
  'attributes.contact.locale': 'Puo',
  'attributes.fallbackHint': 'Kenya | le boleng ba ho kgutlela morao kamora lebitso, bakeng sa mang kapa mang ya se nang letho moo: contact.name|moo',
  'attributes.flowFallbackHint': 'Kenya ?? le boleng ba ho kgutlela morao kamora lebitso, ha ho se letho: contact.name ?? moo'
};

export default common;
